import './App.css';
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { isPortalHost, shouldRedirectToPortal, portalUrl } from './config/site';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
// Most pages report success/errors through hooks/use-toast; this renders those messages.
import { Toaster as UiToaster } from './components/ui/toaster';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import HeaderNew from './components/HeaderNew';
import ScrollToTop from './components/ScrollToTop';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import Home from './pages/Home';
import Coverage from './pages/Coverage';
import LegalPage from './pages/LegalPage';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import About from './pages/About';
import Resources from './pages/Resources';
import ResourceDetail from './pages/ResourceDetail';
import RegulatoryRequirementsList from './pages/RegulatoryRequirementsList';
import SIPTrunking from './pages/products/SIPTrunking';
import SMS from './pages/products/SMS';
import CloudPBX from './pages/products/CloudPBX';
import SipTrunkOrder from './pages/SipTrunkOrder';
import FreeTrial from './pages/FreeTrial';
import AIReceptionist from './pages/products/AIReceptionist';
import VoiceOTP from './pages/products/VoiceOTP';
import Softphone from './pages/products/Softphone';
import TeamsPhone from './pages/products/TeamsPhone';
import Enterprise from './pages/solutions/Enterprise';
import ContactCenters from './pages/solutions/ContactCenters';
import UCaaS from './pages/solutions/UCaaS';
import IoT from './pages/solutions/IoT';

// Phone Numbers Pages
import AllPhoneNumbers from './pages/phone-numbers/AllPhoneNumbers';
import DIDInternationalNumbers from './pages/phone-numbers/DIDInternationalNumbers';
import LocalNumbers from './pages/phone-numbers/LocalNumbers';
import NationalNumbers from './pages/phone-numbers/NationalNumbers';
import MobileNumbers from './pages/phone-numbers/MobileNumbers';
import TwoWayNumbers from './pages/phone-numbers/TwoWayNumbers';
import TollFreeNumbers from './pages/phone-numbers/TollFreeNumbers';
import UIFNNumbers from './pages/phone-numbers/UIFNNumbers';
import CountryDetails from './pages/phone-numbers/CountryDetails';
import RegulatoryRequirements from './pages/phone-numbers/RegulatoryRequirements';


// Sends /admin and /customer paths on the main site to the portal subdomain.
const RedirectToPortal = () => {
  const target = portalUrl(window.location.pathname + window.location.search);
  const external = /^https?:\/\//.test(target);
  useEffect(() => {
    if (external) window.location.replace(target);
  }, [external, target]);
  return external ? null : <Navigate to="/" replace />;
};

// The same source builds two apps (REACT_APP_TARGET):
//   site   -> voip2.co.uk: public pages only; the portal module below is not bundled at all
//   portal -> portal.voip2.co.uk: admin panel and customer portal only
//   unset  -> both, chosen by hostname (local development)
// The comparison must stay a literal process.env check so the build can drop the portal module.
const portalModule = process.env.REACT_APP_TARGET !== 'site' ? require('./portal/portalRoutes') : null;
const portalRoutes = portalModule ? portalModule.portalRoutes : null;
const PortalProviders = portalModule ? portalModule.PortalProviders : React.Fragment;
const PORTAL_ONLY = process.env.REACT_APP_TARGET === 'portal';

function App() {
  const portal = PORTAL_ONLY || (portalModule !== null && isPortalHost());
  const redirectPortalPaths = !portalModule || shouldRedirectToPortal();


  return (
    <HelmetProvider>
      <PortalProviders>
          <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {portal ? (
                <>
                  {portalRoutes}
                  {/* Portal host serves only the portal - everything else goes to customer login */}
                  <Route path="*" element={<Navigate to="/customer/login" replace />} />
                </>
              ) : (
                <>
                  {redirectPortalPaths ? (
                    <>
                      {/* Portal paths live on the portal subdomain in production */}
                      <Route path="/admin/*" element={<RedirectToPortal />} />
                      <Route path="/customer/*" element={<RedirectToPortal />} />
                      <Route path="/sign/*" element={<RedirectToPortal />} />
                    </>
                  ) : (
                    portalRoutes
                  )}

              {/* Public Routes with Header/Footer */}
              <Route
                path="*"
                element={
                  <>
                    <HeaderNew />
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="coverage" element={<Coverage />} />
                      <Route path="pricing" element={<Pricing />} />
                      <Route path="regulatory-requirements" element={<RegulatoryRequirementsList />} />
                      <Route path="contact" element={<Contact />} />
                      <Route path="about" element={<About />} />
                      <Route path="resources" element={<Resources />} />
                      <Route path="resources/:slug" element={<ResourceDetail />} />

                      {/* Phone Numbers */}
                      <Route path="phone-numbers/all" element={<AllPhoneNumbers />} />
                      <Route path="phone-numbers/did-international" element={<DIDInternationalNumbers />} />
                      <Route path="phone-numbers/local" element={<LocalNumbers />} />
                      <Route path="phone-numbers/national" element={<NationalNumbers />} />
                      <Route path="phone-numbers/mobile" element={<MobileNumbers />} />
                      <Route path="phone-numbers/2way" element={<TwoWayNumbers />} />
                      <Route path="phone-numbers/toll-free" element={<TollFreeNumbers />} />
                      <Route path="phone-numbers/uifn" element={<UIFNNumbers />} />
                      <Route path="country/:country" element={<CountryDetails />} />
                      <Route path="country/:country/regulatory-requirements" element={<RegulatoryRequirements />} />

                      {/* Products */}
                      <Route path="products/sip-trunking" element={<SIPTrunking />} />
                      <Route path="products/sms" element={<SMS />} />
                      <Route path="products/cloud-pbx" element={<CloudPBX />} />
                      <Route path="free-trial" element={<FreeTrial />} />
                      <Route path="sip-trunk/order" element={<SipTrunkOrder />} />
                      <Route path="products/ai-receptionist" element={<AIReceptionist />} />
                      <Route path="products/voice-otp" element={<VoiceOTP />} />
                      <Route path="products/softphone" element={<Softphone />} />
                      <Route path="products/teams-phone-system" element={<TeamsPhone />} />

                      {/* Solutions */}
                      <Route path="solutions/enterprise" element={<Enterprise />} />
                      <Route path="solutions/contact-centers" element={<ContactCenters />} />
                      <Route path="solutions/ucaas" element={<UCaaS />} />
                      <Route path="solutions/iot" element={<IoT />} />
                      
                      {/* Legal Pages - MUST BE LAST (catch-all route) */}
                      <Route path=":slug" element={<LegalPage />} />
                    </Routes>
                    <Footer />
                    <CookieConsent />
                  </>
                }
              />
                </>
              )}
            </Routes>
            <Toaster position="top-right" />
            <UiToaster />
            <ToastContainer position="top-right" autoClose={3000} />
          </BrowserRouter>
        </div>
      </PortalProviders>
    </HelmetProvider>
  );
}

export default App;
